/** Project documents, team, status changes and activity. */
import { useState, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Paginator } from "@primereact/ui/paginator";
import { Textarea } from "@primereact/ui/textarea";
import { PROJECT_TRANSITIONS, type ProjectStatus } from "@offertli/shared";
import { StatusBadge } from "../../components/StatusBadge.js";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import {
  CheckboxField,
  Modal,
  SelectInput,
} from "../../components/ui/controls.js";
import { apiGet, apiPatch, apiPut } from "../../lib/api.js";
import {
  ErrorPanel,
  LoadingPanel,
  MutationError,
  Section,
  formatDate,
} from "../shared.js";
import type {
  ProjectActivityPage,
  ProjectDetail,
  ProjectDocument,
} from "./types.js";

/** Transitions that deserve a reason in the history. */
export const NEEDS_COMMENT: ProjectStatus[] = ["on_hold", "cancelled"];

export function ProjectDocuments({ id }: { id: string }) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["project-documents", id],
    queryFn: () =>
      apiGet<{ data: ProjectDocument[] }>(`/projects/${id}/documents`).then(
        (response) => response.data,
      ),
  });
  if (query.isLoading) return <LoadingPanel />;
  if (query.error) return <ErrorPanel error={query.error} />;
  return (
    <Section title={t("projects.documents")}>
      {query.data?.length ? (
        <ul className="divide-y divide-app-border">
          {query.data.map((document) => (
            <li
              key={`${document.type}-${document.id}`}
              className="flex items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <Link
                  className="block truncate font-medium text-brand hover:underline"
                  to={`/${document.type}s/${document.id}`}
                >
                  {document.title || document.number}
                </Link>
                <p className="text-xs text-app-muted">
                  {t(`projects.documentType.${document.type}`)} ·{" "}
                  {document.number ?? "—"} · {formatDate(document.createdAt)}
                </p>
              </div>
              <StatusBadge status={document.status} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-app-muted">{t("projects.noDocuments")}</p>
      )}
    </Section>
  );
}

export function ProjectActivity({ id }: { id: string }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [includeChildren, setIncludeChildren] = useState(false);
  const pageSize = 25;
  const query = useQuery({
    queryKey: ["project-activity", id, page, includeChildren],
    queryFn: () =>
      apiGet<ProjectActivityPage>(`/projects/${id}/activity`, {
        query: { page, pageSize, includeChildren },
      }),
  });
  if (query.isLoading) return <LoadingPanel />;
  if (query.error) return <ErrorPanel error={query.error} />;
  return (
    <Section title={t("projects.activity")}>
      <div className="mb-3">
        <CheckboxField
          id="activity-children"
          checked={includeChildren}
          onChange={(value) => {
            setIncludeChildren(value);
            setPage(1);
          }}
          label={t("projects.includeChildren")}
        />
      </div>
      <ul className="divide-y divide-app-border text-sm">
        {(query.data?.data ?? []).map((entry) => (
          <li key={entry.id} className="py-2">
            <p>{entry.summary}</p>
            <p className="text-xs text-app-muted">
              {entry.actor} · {formatDate(entry.createdAt)} · {entry.event}
            </p>
          </li>
        ))}
      </ul>
      {(query.data?.total ?? 0) > pageSize ? (
        <Paginator.Root
          className="mt-3"
          page={page}
          total={query.data?.total ?? 0}
          itemsPerPage={pageSize}
          onPageChange={(event: { value: number }) => setPage(event.value)}
        />
      ) : null}
    </Section>
  );
}

export type Member = {
  userId: string;
  firstName: string;
  lastName: string;
  role: "manager" | "member";
};

export function ProjectTeam({
  project,
  canManage,
}: {
  project: ProjectDetail;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [editing, setEditing] = useState<Record<string, string> | null>(null);
  const members = useQuery({
    queryKey: ["project-members", project.id],
    queryFn: () =>
      apiGet<{ data: Member[] }>(`/projects/${project.id}/members`).then(
        (response) => response.data,
      ),
  });
  const candidates = useQuery({
    queryKey: ["project-member-candidates", project.id],
    enabled: editing !== null,
    queryFn: () =>
      apiGet<{
        data: Array<{ id: string; firstName: string; lastName: string }>;
      }>(`/projects/${project.id}/member-candidates`).then(
        (response) => response.data,
      ),
  });
  const save = useMutation({
    mutationFn: (roles: Record<string, string>) =>
      apiPut(`/projects/${project.id}/members`, {
        version: project.version,
        managerIds: Object.keys(roles).filter((id) => roles[id] === "manager"),
        memberIds: Object.keys(roles).filter((id) => roles[id] === "member"),
      }),
    onSuccess: async () => {
      setEditing(null);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["project-members", project.id] }),
        client.invalidateQueries({ queryKey: ["project", project.id] }),
      ]);
    },
  });
  const openEditor = () =>
    setEditing(
      Object.fromEntries(
        (members.data ?? []).map((member) => [member.userId, member.role]),
      ),
    );
  return (
    <Section
      title={t("projects.team")}
      actions={
        canManage ? (
          <Button
            type="button"
            size="small"
            variant="outlined"
            severity="secondary"
            icon="pi pi-users"
            onClick={openEditor}
          >
            {t("common.edit")}
          </Button>
        ) : undefined
      }
    >
      {members.isLoading ? (
        <LoadingPanel />
      ) : members.data?.length ? (
        <ul className="space-y-2 text-sm">
          {members.data.map((member) => (
            <li
              key={member.userId}
              className="flex items-center justify-between"
            >
              <span>
                {member.firstName} {member.lastName}
              </span>
              <span className="text-xs text-app-muted">
                {t(`projects.${member.role}`)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-app-muted">{t("projects.noMembers")}</p>
      )}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={t("projects.team")}
        footer={
          <>
            <Button
              type="button"
              variant="outlined"
              severity="secondary"
              onClick={() => setEditing(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              loading={save.isPending}
              onClick={() => editing && save.mutate(editing)}
            >
              {t("common.save")}
            </Button>
          </>
        }
      >
        <MutationError error={save.error} />
        {candidates.isLoading ? (
          <LoadingPanel />
        ) : (
          <ul className="max-h-96 space-y-2 overflow-y-auto">
            {(candidates.data ?? []).map((candidate) => (
              <li
                key={candidate.id}
                className="grid grid-cols-[1fr_9rem] items-center gap-3 text-sm"
              >
                <span>
                  {candidate.firstName} {candidate.lastName}
                </span>
                <SelectInput
                  ariaLabel={`${candidate.firstName} ${candidate.lastName}`}
                  value={editing?.[candidate.id] ?? "none"}
                  onChange={(role) =>
                    setEditing((current) => {
                      const next = { ...(current ?? {}) };
                      if (role === "none") delete next[candidate.id];
                      else next[candidate.id] = role;
                      return next;
                    })
                  }
                  options={[
                    { value: "none", label: "—" },
                    { value: "member", label: t("projects.member") },
                    { value: "manager", label: t("projects.manager") },
                  ]}
                />
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </Section>
  );
}

export function StatusChange({
  project,
  onDone,
}: {
  project: ProjectDetail;
  onDone: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<ProjectStatus | null>(null);
  const [comment, setComment] = useState("");
  const change = useMutation({
    mutationFn: ({ status, note }: { status: ProjectStatus; note?: string }) =>
      apiPatch(`/projects/${project.id}/status`, {
        version: project.version,
        status,
        ...(note ? { comment: note } : {}),
      }),
    onSuccess: async () => {
      setPending(null);
      setComment("");
      await onDone();
    },
  });
  const transitions = PROJECT_TRANSITIONS[project.status];
  return (
    <>
      {transitions.map((next, index) => (
        <Button
          key={next}
          type="button"
          variant={index === 0 && next !== "cancelled" ? undefined : "outlined"}
          severity={
            next === "cancelled"
              ? "danger"
              : index === 0
                ? undefined
                : "secondary"
          }
          loading={change.isPending && change.variables?.status === next}
          onClick={() =>
            NEEDS_COMMENT.includes(next)
              ? setPending(next)
              : change.mutate({ status: next })
          }
        >
          {t(`projects.transition.${next}`)}
        </Button>
      ))}
      {change.error && !pending ? (
        <div className="w-full">
          <MutationError error={change.error} />
        </div>
      ) : null}
      <Modal
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending ? t(`projects.transition.${pending}`) : ""}
        footer={
          <>
            <Button
              type="button"
              variant="outlined"
              severity="secondary"
              onClick={() => setPending(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              severity={pending === "cancelled" ? "danger" : undefined}
              disabled={!comment.trim()}
              loading={change.isPending}
              onClick={() =>
                pending &&
                change.mutate({ status: pending, note: comment.trim() })
              }
            >
              {t("common.confirm")}
            </Button>
          </>
        }
      >
        <MutationError error={change.error} />
        <Field label={t("projects.statusReason")} required>
          <Textarea
            rows={3}
            className="w-full"
            value={comment}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setComment(event.target.value)
            }
          />
        </Field>
      </Modal>
    </>
  );
}
